import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Copy, Check } from "lucide-react";
import AdminLayout from "../../platform/AdminLayout";
import { api } from "../../lib/api";

const STATUS_STYLE = {
  actif: "bg-emerald-100 text-emerald-800",
  inactif: "bg-amber-100 text-amber-800",
  archive: "bg-nuit-900/10 text-steel",
};
const STATUS_LABEL = { actif: "Actif", inactif: "Inactif", archive: "Archivé" };

const inputCls =
  "w-full rounded-xl border border-nuit-900/15 bg-white px-3.5 py-2.5 text-sm text-nuit-900 placeholder:text-steel/60 focus:border-electric focus:outline-none";

function NewClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", address: "", card_ref: "", description: "", vcard_enabled: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api("/admin/clients", { method: "POST", body: { ...form, email: form.email || null } });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-nuit-950/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Nouveau client">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-7" data-testid="new-client-modal">
        {result ? (
          <div>
            <h2 className="font-display text-xl font-bold text-nuit-900">Client créé !</h2>
            <p className="mt-2 text-sm text-steel">
              La page NFC est en ligne. Programmez cette URL permanente sur la carte — elle ne changera jamais, même si le contenu évolue.
            </p>
            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-xl bg-brume p-3.5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-steel">URL permanente</p>
                  <p className="truncate font-mono text-sm text-nuit-900">{result.client.public_url}</p>
                </div>
                <button onClick={() => copy(result.client.public_url, "url")} data-testid="copy-url-btn"
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-nuit-900 px-3.5 py-2 text-xs font-semibold text-white">
                  {copied === "url" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copier
                </button>
              </div>
              {result.temp_password && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3.5">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">Mot de passe provisoire du commerçant</p>
                    <p className="truncate font-mono text-sm text-nuit-900">{result.temp_password}</p>
                    <p className="mt-1 text-xs text-amber-700">Affiché une seule fois — il a aussi été envoyé par e-mail au commerçant.</p>
                  </div>
                  <button onClick={() => copy(result.temp_password, "pwd")} data-testid="copy-pwd-btn"
                    className="flex shrink-0 items-center gap-1.5 rounded-full bg-nuit-900 px-3.5 py-2 text-xs font-semibold text-white">
                    {copied === "pwd" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copier
                  </button>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-2.5">
              <button onClick={() => { onCreated(); onClose(); }} data-testid="goto-client-btn"
                className="flex-1 rounded-full bg-electric py-3 font-display text-sm font-semibold text-white hover:bg-electric-600">
                Voir la liste des clients
              </button>
              <Link to={`/admin/clients/${result.client.client_id}`} data-testid="configure-client-link"
                className="flex-1 rounded-full border border-nuit-900/15 py-3 text-center font-display text-sm font-semibold text-nuit-900 hover:border-nuit-900/40">
                Configurer sa page
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h2 className="font-display text-xl font-bold text-nuit-900">Nouveau client</h2>
            <p className="mt-1.5 text-sm text-steel">
              La fiche, la page NFC (URL permanente) et les 7 boutons sont créés automatiquement. Si vous
              indiquez un e-mail, un compte commerçant est créé et les identifiants lui sont envoyés.
            </p>
            <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-nuit-900">Nom du commerce *</label>
                <input required minLength={2} className={inputCls} value={form.business_name} onChange={set("business_name")} placeholder="Ex : Café des Arts" data-testid="nc-name" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-nuit-900">Contact (nom prénom)</label>
                <input className={inputCls} value={form.contact_name} onChange={set("contact_name")} placeholder="Marie Dupont" data-testid="nc-contact" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-nuit-900">E-mail (compte commerçant)</label>
                <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="marie@exemple.fr" data-testid="nc-email" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-nuit-900">Téléphone</label>
                <input className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+33 6 12 34 56 78" data-testid="nc-phone" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-nuit-900">Référence carte NFC</label>
                <input className={inputCls} value={form.card_ref} onChange={set("card_ref")} placeholder="NV-001" data-testid="nc-cardref" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-nuit-900">Adresse professionnelle</label>
                <input className={inputCls} value={form.address} onChange={set("address")} placeholder="12 rue des Lilas, 75011 Paris" data-testid="nc-address" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-nuit-900">Description affichée sur la page</label>
                <textarea rows={2} maxLength={500} className={`${inputCls} resize-y`} value={form.description} onChange={set("description")} placeholder="Café de quartier, torréfaction artisanale." data-testid="nc-description" />
              </div>
              <label className="flex items-center gap-2.5 text-sm font-medium text-nuit-900 sm:col-span-2">
                <input type="checkbox" checked={form.vcard_enabled} onChange={set("vcard_enabled")} className="h-4 w-4 accent-electric" data-testid="nc-vcard" />
                Activer le bouton « Enregistrer le contact » (vCard)
              </label>
            </div>
            {error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-700" role="alert" data-testid="nc-error">{error}</p>}
            <div className="mt-6 flex gap-2.5">
              <button type="button" onClick={onClose} className="flex-1 rounded-full border border-nuit-900/15 py-3 font-display text-sm font-semibold text-nuit-900">
                Annuler
              </button>
              <button type="submit" disabled={loading} data-testid="nc-submit"
                className="flex-1 rounded-full bg-electric py-3 font-display text-sm font-semibold text-white hover:bg-electric-600 disabled:opacity-50">
                {loading ? "Création…" : "Créer le client"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function AdminClients() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams({ search, status, page: String(page), limit: "20" });
    api(`/admin/clients?${params}`).then(setData).catch(() => {});
  }, [search, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-nuit-900 sm:text-3xl">Clients</h1>
          <p className="mt-1 text-sm text-steel">{data ? `${data.total} fiche(s)` : "Chargement…"}</p>
        </div>
        <button onClick={() => setShowNew(true)} data-testid="new-client-btn"
          className="flex items-center gap-2 rounded-full bg-electric px-5 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-electric-600">
          <Plus className="h-4 w-4" /> Nouveau client
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-steel" />
          <input className={`${inputCls} !pl-10`} placeholder="Rechercher (nom, commerce, identifiant, e-mail…)"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="clients-search" />
        </div>
        <div className="flex gap-1.5">
          {[["", "Tous"], ["actif", "Actifs"], ["inactif", "Inactifs"], ["archive", "Archivés"]].map(([v, l]) => (
            <button key={v} onClick={() => { setStatus(v); setPage(1); }} data-testid={`filter-${v || "tous"}`}
              className={`rounded-full px-4 py-2.5 font-display text-xs font-semibold transition-colors ${
                status === v ? "bg-nuit-900 text-white" : "border border-nuit-900/15 bg-white text-nuit-900 hover:border-nuit-900/40"
              }`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-3xl border border-nuit-900/10 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="border-b border-nuit-900/10 bg-brume/60 text-left text-xs uppercase tracking-wider text-steel">
                <th className="px-5 py-3.5 font-semibold">Commerce</th>
                <th className="px-5 py-3.5 font-semibold">Contact</th>
                <th className="px-5 py-3.5 font-semibold">Identifiant page</th>
                <th className="px-5 py-3.5 font-semibold">Réf. carte</th>
                <th className="px-5 py-3.5 font-semibold">Statut</th>
                <th className="px-5 py-3.5 font-semibold">Créé le</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-steel">Aucun client. Créez votre première fiche.</td></tr>
              )}
              {data?.items.map((c) => (
                <tr key={c.client_id} className="border-b border-nuit-900/5 transition-colors last:border-0 hover:bg-brume/50">
                  <td className="px-5 py-3.5">
                    <Link to={`/admin/clients/${c.client_id}`} className="font-semibold text-nuit-900 hover:text-electric" data-testid={`client-row-${c.slug}`}>
                      {c.business_name}
                    </Link>
                    {!c.page_enabled && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">page désactivée</span>}
                  </td>
                  <td className="px-5 py-3.5 text-steel">{c.contact_name || "—"}<br /><span className="text-xs">{c.email}</span></td>
                  <td className="px-5 py-3.5 font-mono text-xs text-steel">/p/{c.slug}</td>
                  <td className="px-5 py-3.5 text-steel">{c.card_ref || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${STATUS_STYLE[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                  </td>
                  <td className="px-5 py-3.5 text-steel">{c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between border-t border-nuit-900/10 px-5 py-3.5">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"
              className="rounded-full border border-nuit-900/15 px-4 py-2 text-xs font-semibold text-nuit-900 disabled:opacity-30">← Précédent</button>
            <span className="text-xs text-steel">Page {data.page} / {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="page-next"
              className="rounded-full border border-nuit-900/15 px-4 py-2 text-xs font-semibold text-nuit-900 disabled:opacity-30">Suivant →</button>
          </div>
        )}
      </div>

      {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={load} />}
    </AdminLayout>
  );
}
